import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SplitPanelContent from '../../../components/SplitPanelContent';
import { LayoutModeBadge } from './FormCanvas';
import { validateField } from '../../../utils/formValidation';
import {
  buildConvQuestionScreens,
  buildNameValueMap,
  evaluateOperator as sharedEvalOperator,
} from '../../../utils/convFieldSequence';
import { paymentsService } from '../../../services/paymentsService';

// ── Visibility / dependency helpers ──────────────────────────────────────────
// All operator evaluation now goes through the shared evaluateOperator from
// convFieldSequence.js, which normalises snake_case operators, handles
// isChecked/isNotChecked, and uses case-insensitive contains/startsWith/endsWith.
// Local evalOperator has been removed to prevent divergence.

/**
 * Determine if a field should be visible given current form values.
 */
const isFieldVisible = (element, formValues) => {
  if (!element?.hasVisibilityLogic) return true;
  const sourceVal = formValues?.[element?.visibilitySourceField] ?? '';
  const conditionMet = sharedEvalOperator(
    element?.visibilityOperator || 'equals',
    sourceVal,
    element?.visibilityValue || ''
  );
  const action = element?.visibilityAction || 'show';
  return action === 'show' ? conditionMet : !conditionMet;
};

/**
 * Check cross-field dependency rules and return a dep-error string or null.
 * Uses the shared evaluateOperator so operator semantics are identical to
 * buildConvQuestionScreens / computeFieldState used for visibility.
 */
const validateFieldDeps = (element, value, formValues) => {
  const isEmpty =
    value === undefined || value === null || value === '' ||
    (Array.isArray(value) && value.length === 0);
  for (const dep of (element?.dependencies || [])) {
    if (!dep?.sourceField) continue;
    const sourceVal = formValues?.[dep?.sourceField] ?? '';
    const conditionMet = sharedEvalOperator(dep?.operator, sourceVal, dep?.value);
    if (conditionMet) {
      if (dep?.action === 'require' && isEmpty) {
        return dep?.errorMessage || 'This field is required based on another field';
      }
      if (dep?.action === 'setError') {
        return dep?.errorMessage || 'This field has an error based on another field';
      }
    }
  }
  return null;
};

/**
 * Compute default values for slider, rating, and scale fields — mirrors the
 * public form viewer so the builder preview and live form start identically.
 */
const computeFieldDefaults = (formElements) => {
  const fieldDefaults = {};
  for (const field of (formElements || [])) {
    const id = field?.id || field?.name;
    if (!id) continue;
    if (field?.type === 'slider') {
      const min = field?.sliderMin ?? field?.min ?? 0;
      const max = field?.sliderMax ?? field?.max ?? 100;
      fieldDefaults[id] = field?.sliderDefault ?? Math.round((min + max) / 2);
    } else if (field?.type === 'rating') {
      const ratingMax = field?.ratingMax ?? 5;
      fieldDefaults[id] = field?.ratingDefault ?? Math.ceil(ratingMax / 2);
    } else if (field?.type === 'scale') {
      const scaleMin = field?.scaleMin ?? 1;
      const scaleMax = field?.scaleMax ?? 10;
      fieldDefaults[id] = field?.scaleDefault ?? Math.round((scaleMin + scaleMax) / 2);
    }
  }
  return fieldDefaults;
};

// ── Component ────────────────────────────────────────────────────────────────

const darkenHex = (hex, amount = 40) => {
  const h = (hex || '#0e7572').replace('#', '');
  const num = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount);
  const b = Math.max(0, (num & 0xff) - amount);
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
};

// ── Conversational signature pad (mirrors the live form's ConvSignaturePad) ───
const PreviewConvSignaturePad = ({ value, onChange, borderColor, strokeColor }) => {
  const canvasRef = React.useRef(null);
  const drawingRef = React.useRef(false);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = strokeColor || '#111827';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [strokeColor]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };
  const startDraw = (e) => {
    drawingRef.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    const p = getPos(e);
    ctx?.beginPath(); ctx?.moveTo(p.x, p.y);
  };
  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    const p = getPos(e);
    ctx?.lineTo(p.x, p.y); ctx?.stroke();
  };
  const endDraw = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange(canvasRef.current?.toDataURL() || '');
  };
  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    onChange('');
  };

  return (
    <div className="mt-2 space-y-2">
      <canvas
        ref={canvasRef} width={480} height={160}
        className="w-full rounded-xl border-2 cursor-crosshair bg-white/60"
        style={{ borderColor: borderColor || '#d1d5db', touchAction: 'none' }}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
      />
      {value && (
        <button type="button" onClick={clear}
          className="text-xs opacity-50 hover:opacity-80 transition-opacity flex items-center gap-1">
          Clear signature
        </button>
      )}
    </div>
  );
};

const PreviewModal = ({ isOpen, onClose, formElements, formTitle, globalTheme = {}, multiStepConfig, onUpdateTheme, formId, paymentEnabled, paymentSettings, layoutMode, splitPanelConfig }) => {
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Payment preview state
  const [previewPaymentStep, setPreviewPaymentStep] = useState('form'); // 'form' | 'payment'
  const [previewPaymentAmount, setPreviewPaymentAmount] = useState('');

  // Conversational mode state
  const [convFieldIndex, setConvFieldIndex] = useState(0);
  const [convDirection, setConvDirection] = useState('forward');
  const [convShake, setConvShake] = useState(false);
  const [convOutgoing, setConvOutgoing] = useState(null);
  const [convReviewMode, setConvReviewMode] = useState(false); // show answer-review overlay
  const [convConfirmRestart, setConvConfirmRestart] = useState(false); // restart confirmation popover
  const autoAdvanceTimerRef = useRef(null);
  const convOutgoingTimerRef = useRef(null);
  const convReviewTimerRef = useRef(null);   // cancellable timer for auto-open review overlay
  const convFieldIndexRef = useRef(0);       // mirrors convFieldIndex for timer guard callbacks
  const convAdvanceRef = useRef(null);
  const convBackRef = useRef(null);
  const convContextRef = useRef(null);

  // Inject conversational CSS keyframes once (shared with live form; skip if already injected)
  useEffect(() => {
    if (document.getElementById('conv-mode-styles')) return;
    const s = document.createElement('style');
    s.id = 'conv-mode-styles';
    s.textContent = `
      @keyframes convSlideInUp   { from { opacity:0; transform:translateY(52px);  } to { opacity:1; transform:translateY(0); } }
      @keyframes convSlideInDown { from { opacity:0; transform:translateY(-52px); } to { opacity:1; transform:translateY(0); } }
      @keyframes convExitUp      { from { opacity:1; transform:translateY(0);     } to { opacity:0; transform:translateY(-52px); } }
      @keyframes convExitDown    { from { opacity:1; transform:translateY(0);     } to { opacity:0; transform:translateY(52px);  } }
      @keyframes convShakeAnim   { 0%,100%{transform:translateX(0)} 15%,45%,75%{transform:translateX(-10px)} 30%,60%,90%{transform:translateX(10px)} }
      .conv-in-up    { animation: convSlideInUp   0.38s cubic-bezier(0.22,0.61,0.36,1) both; }
      .conv-in-down  { animation: convSlideInDown 0.38s cubic-bezier(0.22,0.61,0.36,1) both; }
      .conv-exit-up  { animation: convExitUp      0.35s cubic-bezier(0.22,0.61,0.36,1) both; pointer-events:none; }
      .conv-exit-down{ animation: convExitDown    0.35s cubic-bezier(0.22,0.61,0.36,1) both; pointer-events:none; }
      .conv-shake    { animation: convShakeAnim   0.5s ease both; }
    `;
    document.head.appendChild(s);
  }, []);

  // Keyboard handler — scoped to the conversational preview container (not window).
  // Defined once at component level; reads advance/back/context from stable refs
  // so it never needs to be recreated. Attached via onKeyDown on the container div.
  const convContainerRef = useRef(null);
  const handleConvKeyDown = (e) => {
    if (!convAdvanceRef.current) return;
    const tag     = document.activeElement?.tagName?.toLowerCase();
    const activeEl = document.activeElement;

    if (e.key === 'Enter') {
      if (tag === 'textarea' || tag === 'select' || tag === 'button') return;
      e.preventDefault();
      convAdvanceRef.current();
      return;
    }
    if ((e.key === 'Backspace' || e.key === 'ArrowLeft') && convBackRef.current) {
      if (tag === 'input') {
        if (activeEl?.value?.length > 0) return;
      } else if (tag === 'textarea' || tag === 'select') { return; }
      e.preventDefault();
      convBackRef.current();
      return;
    }
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && convContextRef.current) {
      const { currentField: cf, currentFormValues, onHandleChange } = convContextRef.current;
      if (!cf || !['radio', 'select', 'dropdown'].includes(cf?.type)) return;
      const opts = (cf?.options || []).map(o => (typeof o === 'object' ? o?.value : o));
      if (!opts.length) return;
      e.preventDefault();
      const fid     = cf?.id || cf?.name;
      const currIdx = opts.indexOf(currentFormValues?.[fid]);
      const nextIdx = e.key === 'ArrowDown'
        ? (currIdx + 1) % opts.length
        : (currIdx - 1 + opts.length) % opts.length;
      onHandleChange(fid, opts[nextIdx]);
    }
  };

  // Keep convFieldIndexRef in sync with convFieldIndex so timer callbacks can
  // guard against stale closures without reading React state directly.
  useEffect(() => { convFieldIndexRef.current = convFieldIndex; }, [convFieldIndex]);

  // Reset conversational state whenever the modal transitions from closed → open,
  // so creators always start at question 1 regardless of HMR re-renders or
  // repeated opens mid-form.
  useEffect(() => {
    if (!isOpen) return;
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    if (convOutgoingTimerRef.current) {
      clearTimeout(convOutgoingTimerRef.current);
      convOutgoingTimerRef.current = null;
    }
    if (convReviewTimerRef.current) {
      clearTimeout(convReviewTimerRef.current);
      convReviewTimerRef.current = null;
    }
    convFieldIndexRef.current = 0;
    setConvFieldIndex(0);
    setConvDirection('forward');
    setConvShake(false);
    setConvOutgoing(null);
    setConvReviewMode(false);
    setConvConfirmRestart(false);
  }, [isOpen]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      if (convOutgoingTimerRef.current) clearTimeout(convOutgoingTimerRef.current);
      if (convReviewTimerRef.current) clearTimeout(convReviewTimerRef.current);
    };
  }, []);

  // Seed default values for slider, rating, and scale fields whenever the modal
  // opens or the field list changes — mirrors the public form viewer so that the
  // builder preview and the live form always start with the same pre-selected value.
  useEffect(() => {
    if (!isOpen) return;
    const fieldDefaults = computeFieldDefaults(formElements);
    if (Object.keys(fieldDefaults).length === 0) return;
    setFormValues(prev => {
      const patch = {};
      for (const [id, val] of Object.entries(fieldDefaults)) {
        if (prev[id] == null) patch[id] = val;
      }
      if (Object.keys(patch).length === 0) return prev;
      return { ...prev, ...patch };
    });
  }, [isOpen, formElements]);

  if (!isOpen) return null;

  // Derive isMultiStep from layoutMode (top-level) or fall back to multiStepConfig.enabled
  const isMultiStep = layoutMode === 'multi-step'
    ? (multiStepConfig?.enabled && (multiStepConfig?.steps?.length || 0) > 0)
    : (layoutMode == null && multiStepConfig?.enabled && (multiStepConfig?.steps?.length || 0) > 0);
  // Conversational: new layoutMode flag takes precedence; fall back to legacy toggle.
  const isConversational =
    (layoutMode === 'conversational' || (!layoutMode && multiStepConfig?.conversationalMode === true))
    && !(formElements || []).some(f => f?.type === 'file');
  const isSplit = layoutMode === 'split';
  const steps = multiStepConfig?.steps || [];
  const safeStep = Math.min(currentStep, Math.max(0, steps?.length - 1));

  // Get elements for a given step (or all if not multi-step)
  const getStepElements = (step) => {
    if (!step) return formElements || [];
    const assignedIds = step?.fieldIds || [];
    const stepEls = (formElements || [])?.filter(el => assignedIds?.includes(el?.id));
    return stepEls;
  };

  // Unassigned elements go on the last step in preview
  const getUnassignedElements = () => {
    if (!isMultiStep) return [];
    const allAssigned = steps?.flatMap(s => s?.fieldIds || []);
    return (formElements || [])?.filter(el => !allAssigned?.includes(el?.id));
  };

  // Elements to show on current step
  const currentStepObj = steps?.[safeStep];
  const stepElements = isMultiStep
    ? [
        ...getStepElements(currentStepObj),
        ...(safeStep === steps?.length - 1 ? getUnassignedElements() : []),
      ]
    : formElements || [];

  // Determine which fields are currently visible
  const visibleElements = stepElements?.filter((el) => isFieldVisible(el, formValues));

  const handleChange = (name, value) => {
    if (!name) return;
    const newValues = { ...formValues, [name]: value };
    setFormValues(newValues);
    // Clear the error for the field that just changed, AND clear errors for
    // any fields that became hidden as a result of this value change.
    setErrors((prev) => {
      if (!Object.keys(prev).length) return prev;
      const next = { ...prev };
      // Always clear the changed field's own error
      delete next[name];
      // Clear errors for fields now hidden by the updated values
      (formElements || []).forEach((el) => {
        const fieldId = el?.name || el?.id;
        if (fieldId && next[fieldId] && !isFieldVisible(el, newValues)) {
          delete next[fieldId];
        }
      });
      return next;
    });
  };

  // Run field-level + dependency validation for a single element; return error or null.
  // Mirrors how the live viewer passes { ...f, required: state.required } to validateField
  // so that dependency-driven required fields use the field's own requiredMessage.
  const validateOne = (el, values) => {
    const fieldId = el?.name || el?.id;
    const value = values?.[fieldId];

    // Compute effective required: start with the field's own flag, then check if any
    // 'require' dependency condition is currently met.
    let effectiveRequired = el?.required || false;
    if (!effectiveRequired) {
      for (const dep of (el?.dependencies || [])) {
        if (!dep?.sourceField || dep?.action !== 'require') continue;
        const srcVal = values?.[dep?.sourceField] ?? '';
        if (sharedEvalOperator(dep?.operator, srcVal, dep?.value)) {
          effectiveRequired = true;
          break;
        }
      }
    }

    // Pass effective required into validateField so the custom requiredMessage fires.
    return (
      validateField({ ...el, required: effectiveRequired }, value) ||
      // Only check setError deps here — require deps are already handled above.
      validateFieldDeps(el, value, values) ||
      null
    );
  };

  const validateCurrentStep = () => {
    if (!multiStepConfig?.validateOnNext) return true;
    const newErrors = {};
    visibleElements?.forEach((el) => {
      const fieldId = el?.name || el?.id;
      if (!fieldId) return;
      const err = validateOne(el, formValues);
      if (err) newErrors[fieldId] = err;
    });
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    setErrors({});
    setCurrentStep(s => Math.min(steps?.length - 1, s + 1));
  };

  const handlePrev = () => {
    setErrors({});
    setCurrentStep(s => Math.max(0, s - 1));
  };

  const handleSubmit = async () => {
    const newErrors = {};
    // Validate all visible elements across all steps
    const allVisible = (formElements || [])?.filter(el => isFieldVisible(el, formValues));
    allVisible?.forEach((el) => {
      const fieldId = el?.name || el?.id;
      if (!fieldId) return;
      const err = validateOne(el, formValues);
      if (err) newErrors[fieldId] = err;
    });
    setErrors(newErrors);
    if (Object.keys(newErrors)?.length > 0) return;

    // If payment is enabled, show the mock payment step (no real API call in preview)
    if (paymentEnabled) {
      const settings = paymentSettings || {};
      if (settings?.mode === 'variable') {
        const amount = parseFloat(previewPaymentAmount);
        if (!amount || isNaN(amount) || amount <= 0) {
          setErrors({ _payment: 'Please enter a valid payment amount.' });
          return;
        }
      }
      setPreviewPaymentStep('payment');
    } else {
      setSubmitted(true);
    }
  };

  // Validate a single field on blur and surface the error immediately.
  const handleBlur = (el) => {
    const fieldId = el?.name || el?.id;
    if (!fieldId) return;
    const err = validateOne(el, formValues);
    setErrors((prev) => {
      if (!err && !prev[fieldId]) return prev;
      if (!err) { const next = { ...prev }; delete next[fieldId]; return next; }
      return { ...prev, [fieldId]: err };
    });
  };

  const handleReset = () => {
    // Cancel any pending auto-advance or outgoing-animation timers so stale
    // callbacks cannot advance the preview after the reset.
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    if (convOutgoingTimerRef.current) {
      clearTimeout(convOutgoingTimerRef.current);
      convOutgoingTimerRef.current = null;
    }
    if (convReviewTimerRef.current) {
      clearTimeout(convReviewTimerRef.current);
      convReviewTimerRef.current = null;
    }
    convFieldIndexRef.current = 0;
    // Re-seed slider/rating/scale defaults so the preview matches the live
    // form's initial state after a restart (instead of clearing to nothing).
    setFormValues(computeFieldDefaults(formElements));
    setErrors({});
    setSubmitted(false);
    setCurrentStep(0);
    setPreviewPaymentStep('form');
    setPreviewPaymentAmount('');
    setConvFieldIndex(0);
    setConvDirection('forward');
    setConvShake(false);
    setConvOutgoing(null);
    setConvReviewMode(false);
    setConvConfirmRestart(false);
  };

  // Shared confirm-or-reset entry point for every conversational-mode control
  // that can clear in-progress answers (header Restart + footer Reset).
  // Resets immediately only when the creator is still on the first question
  // (nothing entered yet); otherwise asks for confirmation first.
  const requestConvReset = () => {
    if (convFieldIndex === 0) {
      handleReset();
      return;
    }
    setConvConfirmRestart(true);
  };

  // Check if a field is disabled by a dependency
  const isFieldDisabled = (element) => {
    for (const dep of (element?.dependencies || [])) {
      if (!dep?.sourceField) continue;
      const sourceVal = formValues?.[dep?.sourceField] ?? '';
      const conditionMet = sharedEvalOperator(dep?.operator, sourceVal, dep?.value);
      if (conditionMet && dep?.action === 'disable') return true;
    }
    return false;
  };

  const renderPreviewElement = (element) => {
    const name = element?.name || element?.id;
    const value = formValues?.[name] ?? '';
    const error = errors?.[name];
    const disabled = isFieldDisabled(element);

    const gt = globalTheme || {};
    const fs = element?.fieldStyle || {};

    // Build styles from globalTheme + fieldStyle
    const inputStyle = {
      backgroundColor: fs?.inputBg || gt?.inputBackground || undefined,
      borderColor: error ? undefined : (fs?.borderColor || gt?.accentColor || undefined),
      borderRadius: fs?.borderRadius != null
        ? `${fs?.borderRadius}px`
        : gt?.borderRadius != null
        ? `${gt?.borderRadius}px`
        : undefined,
      color: fs?.textColor || gt?.inputTextColor || undefined,
      fontFamily: gt?.fontFamily || undefined,
      fontSize: fs?.fontSize != null
        ? `${fs?.fontSize}px`
        : gt?.fontSize
        ? `${gt?.fontSize}px`
        : undefined,
      fontWeight: fs?.inputFontWeight || gt?.fontWeight || undefined,
      height: fs?.inputHeight != null ? `${fs?.inputHeight}px` : undefined,
      paddingLeft: fs?.paddingX != null ? `${fs?.paddingX}px` : undefined,
      paddingRight: fs?.paddingX != null ? `${fs?.paddingX}px` : undefined,
      paddingTop: fs?.paddingY != null ? `${fs?.paddingY}px` : undefined,
      paddingBottom: fs?.paddingY != null ? `${fs?.paddingY}px` : undefined,
    };

    const labelStyle = {
      color: fs?.labelColor || gt?.labelColor || undefined,
      fontFamily: gt?.fontFamily || undefined,
      fontWeight: fs?.labelFontWeight || undefined,
      textAlign: fs?.labelTextAlign || undefined,
      fontSize: gt?.fontSize ? `${gt?.fontSize}px` : undefined,
    };

    const wrapperStyle = {
      marginBottom: fs?.marginBottom != null ? `${fs?.marginBottom}px` : undefined,
      marginTop: fs?.marginTop != null ? `${fs?.marginTop}px` : undefined,
    };

    const inputClass = `w-full px-3 py-2 bg-background border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-colors ${
      error ? 'border-destructive focus:ring-destructive' : 'border-input'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

    switch (element?.type) {
      case 'input':
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Label Text'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <input
              type="text"
              name={name}
              value={value}
              disabled={disabled}
              placeholder={element?.placeholder || 'Enter text...'}
              className={inputClass}
              style={inputStyle}
              onChange={(e) => handleChange(name, e?.target?.value)}
              onBlur={() => handleBlur(element)}
            />
            {element?.description && (
              <p className="text-xs text-muted-foreground">{element?.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Paragraph'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <textarea
              name={name}
              value={value}
              disabled={disabled}
              placeholder={element?.placeholder || 'Enter your comments here...'}
              rows={element?.rows || 4}
              className={`${inputClass} resize-none`}
              style={inputStyle}
              onChange={(e) => handleChange(name, e?.target?.value)}
              onBlur={() => handleBlur(element)}
            />
            {element?.description && (
              <p className="text-xs text-muted-foreground">{element?.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );

      case 'select':
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Select Option'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <select
              name={name}
              value={value}
              disabled={disabled}
              className={inputClass}
              style={inputStyle}
              onChange={(e) => handleChange(name, e?.target?.value)}
              onBlur={() => handleBlur(element)}
            >
              <option value="">{element?.placeholder || 'Choose an option...'}</option>
              {(element?.options || [])?.map((opt, idx) => (
                <option key={idx} value={opt}>{opt}</option>
              ))}
            </select>
            {element?.description && (
              <p className="text-xs text-muted-foreground">{element?.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Select Options'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            {element?.description && (
              <p className="text-xs text-muted-foreground -mt-1">{element?.description}</p>
            )}
            <div className="space-y-1.5">
              {(element?.options || ['Option 1', 'Option 2', 'Option 3'])?.map((opt, idx) => {
                const checked = Array.isArray(formValues?.[name])
                  ? formValues?.[name]?.includes(opt)
                  : false;
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={checked}
                      className="w-4 h-4 text-primary focus:ring-2 focus:ring-primary"
                      onChange={(e) => {
                        const current = Array.isArray(formValues?.[name]) ? formValues?.[name] : [];
                        const next = e?.target?.checked
                          ? [...current, opt]
                          : current?.filter((v) => v !== opt);
                        handleChange(name, next);
                      }}
                      onBlur={() => handleBlur(element)}
                    />
                    <span className="text-sm" style={{ color: labelStyle?.color || undefined, fontFamily: labelStyle?.fontFamily || undefined }}>{opt}</span>
                  </div>
                );
              })}
            </div>
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Select Option'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            {element?.description && (
              <p className="text-xs text-muted-foreground -mt-1">{element?.description}</p>
            )}
            <div className="space-y-1.5">
              {(element?.options || ['Option 1', 'Option 2', 'Option 3'])?.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={name}
                    value={opt}
                    disabled={disabled}
                    checked={value === opt}
                    className="w-4 h-4 text-primary focus:ring-2 focus:ring-primary"
                    onChange={(e) => handleChange(name, e?.target?.value)}
                    onBlur={() => handleBlur(element)}
                  />
                  <span className="text-sm" style={{ color: labelStyle?.color || undefined, fontFamily: labelStyle?.fontFamily || undefined }}>{opt}</span>
                </div>
              ))}
            </div>
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );

      case 'number':
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Number'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <input
              type="number"
              name={name}
              value={value}
              disabled={disabled}
              placeholder={element?.placeholder || '0'}
              className={inputClass}
              style={inputStyle}
              onChange={(e) => handleChange(name, e?.target?.value)}
              onBlur={() => handleBlur(element)}
            />
            {element?.description && (
              <p className="text-xs text-muted-foreground">{element?.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );

      case 'date':
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Date'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <input
              type="date"
              name={name}
              value={value}
              disabled={disabled}
              className={inputClass}
              style={inputStyle}
              onChange={(e) => handleChange(name, e?.target?.value)}
              onBlur={() => handleBlur(element)}
            />
            {element?.description && (
              <p className="text-xs text-muted-foreground">{element?.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );

      case 'email':
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Email'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <input
              type="email"
              name={name}
              value={value}
              disabled={disabled}
              placeholder={element?.placeholder || 'you@example.com'}
              className={inputClass}
              style={inputStyle}
              onChange={(e) => handleChange(name, e?.target?.value)}
              onBlur={() => handleBlur(element)}
            />
            {element?.description && (
              <p className="text-xs text-muted-foreground">{element?.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );

      case 'phone':
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Phone'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <input
              type="tel"
              name={name}
              value={value}
              disabled={disabled}
              placeholder={element?.placeholder || '+1 (555) 000-0000'}
              className={inputClass}
              style={inputStyle}
              onChange={(e) => handleChange(name, e?.target?.value)}
              onBlur={() => handleBlur(element)}
            />
            {element?.description && (
              <p className="text-xs text-muted-foreground">{element?.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );

      case 'toggle': {
        const isOn = !!value;
        return (
          <div className="flex items-center justify-between gap-4" style={wrapperStyle}>
            <div>
              <label className="text-sm font-medium" style={labelStyle}>
                {element?.label || 'Toggle'}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              {element?.description && (
                <p className="text-xs text-muted-foreground">{element?.description}</p>
              )}
              {error && (
                <p className="text-xs text-destructive flex items-center gap-1 mt-0.5">
                  <Icon name="AlertCircle" size={12} />
                  {error}
                </p>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isOn}
              disabled={disabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                isOn ? 'bg-primary' : 'bg-muted'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => {
                if (!disabled) {
                  handleChange(name, !isOn);
                  // Run validation immediately on toggle change
                  const err = validateField({ ...element }, !isOn) || validateFieldDeps(element, !isOn, { ...formValues, [name]: !isOn });
                  setErrors((prev) => {
                    if (!err && !prev[name]) return prev;
                    if (!err) { const next = { ...prev }; delete next[name]; return next; }
                    return { ...prev, [name]: err };
                  });
                }
              }}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isOn ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>
        );
      }

      case 'rating': {
        const ratingMax = Math.max(1, element?.ratingMax ?? 5);
        const ratingDefault = Math.min(ratingMax, Math.max(1, element?.ratingDefault ?? Math.ceil(ratingMax / 2)));
        const currentRating = value !== '' && value != null ? Math.min(ratingMax, Math.max(1, Number(value))) : ratingDefault;
        const starColor = gt?.accentColor || fs?.borderColor || '#0e7572';
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Star Rating'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <div className="flex items-center gap-0.5">
              {Array.from({ length: ratingMax }, (_, i) => {
                const starVal = i + 1;
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleChange(name, starVal)}
                    style={{
                      fontSize: '28px',
                      color: starVal <= currentRating ? starColor : '#d1d5db',
                      background: 'none',
                      border: 'none',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      padding: '0 2px',
                      lineHeight: 1,
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >★</button>
                );
              })}
            </div>
            {element?.description && (
              <p className="text-xs text-muted-foreground">{element?.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );
      }

      case 'scale': {
        const scaleMin = element?.scaleMin ?? 1;
        const scaleMax = Math.max(scaleMin + 1, element?.scaleMax ?? 10);
        const scaleDefault = Math.min(scaleMax, Math.max(scaleMin, element?.scaleDefault ?? Math.round((scaleMin + scaleMax) / 2)));
        const currentScale = value !== '' && value != null ? Math.min(scaleMax, Math.max(scaleMin, Number(value))) : scaleDefault;
        const scaleColor = gt?.accentColor || fs?.borderColor || '#0e7572';
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Numeric Scale'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => {
                const val = scaleMin + i;
                const isSelected = val === currentScale;
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleChange(name, val)}
                    className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: isSelected ? scaleColor : undefined,
                      borderColor: isSelected ? scaleColor : undefined,
                      color: isSelected ? '#fff' : undefined,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    {val}
                  </button>
                );
              })}
            </div>
            {element?.description && (
              <p className="text-xs text-muted-foreground">{element?.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );
      }

      case 'slider': {
        const sliderMin = element?.sliderMin ?? element?.min ?? 0;
        const sliderMax = element?.sliderMax ?? element?.max ?? 100;
        const sliderStep = element?.sliderStep ?? element?.step ?? 1;
        const sliderDefault = element?.sliderDefault ?? Math.round((sliderMin + sliderMax) / 2);
        const sliderVal = value !== '' && value != null ? Number(value) : sliderDefault;
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium" style={labelStyle}>
                {element?.label || 'Slider'}
                {element?.required && <span className="text-destructive ml-1">*</span>}
              </label>
              <span className="text-sm font-medium text-primary">{sliderVal}</span>
            </div>
            <input
              type="range"
              name={name}
              min={sliderMin}
              max={sliderMax}
              step={sliderStep}
              value={sliderVal}
              disabled={disabled}
              className="w-full accent-primary"
              onChange={(e) => handleChange(name, e?.target?.value)}
              onBlur={() => handleBlur(element)}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{sliderMin}</span>
              <span>{sliderMax}</span>
            </div>
            {element?.description && (
              <p className="text-xs text-muted-foreground">{element?.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );
      }

      case 'content':
      case 'heading':
        if (element?.id?.startsWith('heading') || element?.contentType === 'heading' || element?.label === 'Heading' || element?.type === 'heading') {
          return (
            <h3
              className="font-heading font-semibold"
              style={{
                color: element?.labelColor || labelStyle?.color || 'var(--color-foreground)',
                fontFamily: labelStyle?.fontFamily || undefined,
                fontSize: element?.fieldStyle?.fontSize != null
                  ? `${element?.fieldStyle?.fontSize}px`
                  : element?.fontSize
                  ? `${element?.fontSize}px`
                  : '1.25rem',
                fontWeight: element?.fontWeight || '600',
                textAlign: element?.fieldStyle?.labelTextAlign || element?.textAlign || undefined,
                ...wrapperStyle,
              }}
            >
              {element?.content || element?.label || 'Heading Text'}
            </h3>
          );
        }
        // paragraph content
        return (
          <p className="text-sm leading-relaxed" style={{ color: labelStyle?.color || 'var(--color-foreground)', fontFamily: labelStyle?.fontFamily || undefined, ...wrapperStyle }}>
            {element?.content || 'Paragraph text goes here.'}
          </p>
        );

      case 'paragraph':
        return (
          <p className="text-sm leading-relaxed" style={{ color: labelStyle?.color || 'var(--color-foreground)', fontFamily: labelStyle?.fontFamily || undefined, ...wrapperStyle }}>
            {element?.content || element?.label || 'Paragraph text goes here.'}
          </p>
        );

      case 'divider':
        return <hr style={{ borderColor: gt?.accentColor ? `${gt.accentColor}40` : undefined, ...wrapperStyle }} />;

      case 'media': {
        const imgSize = element?.fieldStyle?.imageSize ?? element?.imageSize ?? 100;
        return (
          <div className="w-full overflow-hidden rounded-md flex justify-center" style={wrapperStyle}>
            <img
              src={element?.src || '/assets/images/web-forms-logo-1769800970615.png'}
              alt={element?.alt || 'Form header image'}
              style={{ width: `${imgSize}%`, objectFit: 'contain', display: 'block' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        );
      }

      // file / signature: render a placeholder — they accept no text validation rules
      case 'file':
      case 'signature':
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || (element?.type === 'file' ? 'File Upload' : 'Signature')}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <div className={`flex items-center justify-center h-20 rounded-md border-2 border-dashed text-xs text-muted-foreground ${error ? 'border-destructive' : 'border-input'}`}>
              <Icon name={element?.type === 'file' ? 'Upload' : 'PenTool'} size={16} className="mr-2" />
              {element?.type === 'file' ? 'File upload (preview only)' : 'Signature pad (preview only)'}
            </div>
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );

      // Generic fallback — renders a text input with full validation so any future
      // field type gets blur-validation automatically.
      default:
        return (
          <div className="space-y-1.5" style={wrapperStyle}>
            <label className="text-sm font-medium block" style={labelStyle}>
              {element?.label || 'Field'}
              {element?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <input
              type="text"
              name={name}
              value={value}
              disabled={disabled}
              placeholder={element?.placeholder || ''}
              className={inputClass}
              style={inputStyle}
              onChange={(e) => handleChange(name, e?.target?.value)}
              onBlur={() => handleBlur(element)}
            />
            {element?.description && (
              <p className="text-xs text-muted-foreground">{element?.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {error}
              </p>
            )}
          </div>
        );
    }
  };

  // ── Footer bar derived values (hoisted so JSX stays clean) ─────────────────
  const progressStyle = multiStepConfig?.progressStyle || 'bar';
  const isFooterBar = isMultiStep && progressStyle === 'footer-bar';
  const footerBarBg = globalTheme?.footerColor || darkenHex(globalTheme?.accentColor || '');
  const isFirstStep = safeStep === 0;
  const isLastStep = safeStep === steps?.length - 1;
  const showStepCountFB = multiStepConfig?.showStepCount !== false;

  // Build form container style from globalTheme
  const gt = globalTheme || {};
  const bgType = gt?.backgroundType || 'solid';
  let formBackground;
  if (bgType === 'gradient') {
    const dir = gt?.gradientDirection || 'to bottom';
    const c1 = gt?.gradientColor1 || '#f0fdf4';
    const c2 = gt?.gradientColor2 || '#dcfce7';
    formBackground = `linear-gradient(${dir}, ${c1}, ${c2})`;
  } else if (bgType === 'image' && gt?.backgroundImage) {
    formBackground = `url(${gt?.backgroundImage}) center/cover no-repeat`;
  } else if (bgType === 'solid' && gt?.backgroundColor) {
    formBackground = gt?.backgroundColor;
  }

  const formContainerStyle = {
    background: formBackground || undefined,
    fontFamily: gt?.fontFamily || undefined,
  };

  // ── Multi-step progress renderer ─────────────────────────────────────────
  const renderProgress = () => {
    if (!isMultiStep) return null;
    const progressStyle = multiStepConfig?.progressStyle || 'bar';
    const showStepCount = multiStepConfig?.showStepCount !== false;
    const showStepTitles = multiStepConfig?.showStepTitles !== false;
    // footer-bar and none have no top/bottom progress widget
    if (progressStyle === 'none' || progressStyle === 'footer-bar') return null;

    return (
      <div className="mb-4">
        {progressStyle === 'bar' && (
          <div className="space-y-1">
            {showStepCount && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Step {safeStep + 1} of {steps?.length}</span>
                <span>{Math.round(((safeStep + 1) / steps?.length) * 100)}%</span>
              </div>
            )}
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${((safeStep + 1) / steps?.length) * 100}%` }}
              />
            </div>
          </div>
        )}
        {progressStyle === 'steps' && (
          <div className="flex items-center gap-1">
            {steps?.map((step, idx) => (
              <React.Fragment key={step?.id}>
                <button
                  type="button"
                  onClick={() => multiStepConfig?.allowStepJump && setCurrentStep(idx)}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                    idx < safeStep ? 'bg-primary text-primary-foreground' :
                    idx === safeStep ? 'bg-primary text-primary-foreground ring-2 ring-primary/30' : 'bg-muted text-muted-foreground'
                  } ${multiStepConfig?.allowStepJump ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                  title={showStepTitles ? step?.title : undefined}
                >
                  {idx < safeStep ? <Icon name="Check" size={12} /> : idx + 1}
                </button>
                {idx < steps?.length - 1 && (
                  <div className={`flex-1 h-0.5 transition-colors ${idx < safeStep ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
        {progressStyle === 'numbered' && (
          <div className="flex items-center gap-2 flex-wrap">
            {steps?.map((step, idx) => (
              <button
                type="button"
                key={step?.id}
                onClick={() => multiStepConfig?.allowStepJump && setCurrentStep(idx)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  idx === safeStep ? 'bg-primary text-primary-foreground' :
                  idx < safeStep ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                } ${multiStepConfig?.allowStepJump ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
              >
                <span>{idx + 1}</span>
                {showStepTitles && <span>{step?.title}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── Conversational preview renderer ─────────────────────────────────────────
  // Called during render (not a component); captures state via closure.
  const renderConvPreview = () => {
    const allPreviewFields = formElements || [];
    const AUTO_ADV = new Set(['radio', 'toggle', 'select', 'dropdown']);

    // Use the shared sequence builder (same logic as the live form renderer).
    // logicOverrides=null since PreviewModal does not run the full logic engine;
    // per-field dependency rules (visibility, required, disabled) still apply via
    // computeFieldState inside buildConvQuestionScreens.
    const nameValueMap = buildNameValueMap(
      allPreviewFields,
      formValues,
      { keyPreference: 'name' },
    );
    const questionScreens = buildConvQuestionScreens(allPreviewFields, nameValueMap, multiStepConfig, null);

    const totalScreens = questionScreens.length;
    const safeIdx = totalScreens > 0 ? Math.min(convFieldIndex, totalScreens - 1) : 0;
    const currentScreen = questionScreens[safeIdx] || { field: null, prelude: [] };
    const currentField = currentScreen?.field;
    const isLastScreen = safeIdx === totalScreens - 1;
    const progressPct = totalScreens > 0 ? ((safeIdx + 1) / totalScreens) * 100 : 0;

    const accent = gt?.accentColor || '#0e7572';
    const labelColor = gt?.labelColor || '#111827';
    const fontFamily = gt?.fontFamily || undefined;
    const submitText = multiStepConfig?.submitButtonText || 'Submit';

    // currentState comes from computeEffectiveFieldState inside buildConvQuestionScreens;
    // use it for required / disabled indicators so dependency-driven overrides are reflected.
    const currentState = currentScreen?.state;
    const currentFid = currentField?.name || currentField?.id;
    const currentFieldValue = formValues?.[currentFid];
    const currentError = errors?.[currentFid];
    const showOkButton = currentField ? !AUTO_ADV.has(currentField?.type) : true;
    const animClass = convDirection === 'backward' ? 'conv-in-down' : 'conv-in-up';

    // Exit animation helper
    const startExitAnimation = (exitClass) => {
      setConvOutgoing({ screen: currentScreen, exitClass });
      if (convOutgoingTimerRef.current) clearTimeout(convOutgoingTimerRef.current);
      convOutgoingTimerRef.current = setTimeout(() => setConvOutgoing(null), 400);
    };

    // Core advance: validates then navigates; accepts explicit overrideValue to avoid
    // stale-closure issues with auto-advance (radio/toggle/select onChange).
    const handleConvAdvanceWithValue = (overrideFieldId, overrideValue) => {
      if (currentField) {
        const fid = overrideFieldId || currentFid;
        // Build effective values: merge override so validateOne sees the just-selected value
        // even when formValues state hasn't been flushed yet (stale closure on auto-advance).
        const effectiveValues = overrideValue !== undefined
          ? { ...formValues, [fid]: overrideValue }
          : formValues;
        // validateOne computes dependency-driven required and runs validateField + dep checks.
        const err = validateOne(currentField, effectiveValues);
        if (err) {
          setErrors(prev => ({ ...prev, [fid]: err }));
          setConvShake(true); setTimeout(() => setConvShake(false), 600); return;
        }
        setErrors(prev => { const n = { ...prev }; delete n[fid]; return n; });
      }
      if (isLastScreen) { handleSubmit(); return; }
      const nextIdx = Math.min(safeIdx + 1, totalScreens - 1);
      startExitAnimation('conv-exit-up');
      setConvDirection('forward');
      setConvFieldIndex(nextIdx);
      // When landing on the last screen, automatically open the review overlay
      // if at least one question has already been answered — mirrors the live form UX.
      if (nextIdx === totalScreens - 1) {
        const effectiveValues = overrideValue !== undefined
          ? { ...formValues, [overrideFieldId || currentFid]: overrideValue }
          : formValues;
        const hasAnswers = Object.values(effectiveValues).some(v =>
          v !== '' && v !== null && v !== undefined &&
          !(Array.isArray(v) && v.length === 0)
        );
        if (hasAnswers) {
          // Cancel any previous pending review-open timer, then schedule a new one.
          // The callback guards against stale state by checking convFieldIndexRef, which
          // is kept in sync with convFieldIndex via a useEffect above.
          if (convReviewTimerRef.current) clearTimeout(convReviewTimerRef.current);
          convReviewTimerRef.current = setTimeout(() => {
            convReviewTimerRef.current = null;
            // Only open review if the creator is still on the intended last screen
            if (convFieldIndexRef.current === nextIdx) {
              setConvReviewMode(true);
            }
          }, 420);
        }
      }
    };

    const handleConvAdvance = () => handleConvAdvanceWithValue(undefined, undefined);

    const handleConvBack = () => {
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      // Cancel a pending auto-review open so navigating back doesn't open the overlay
      if (convReviewTimerRef.current) {
        clearTimeout(convReviewTimerRef.current);
        convReviewTimerRef.current = null;
      }
      if (safeIdx === 0) return;
      startExitAnimation('conv-exit-down');
      setConvDirection('backward');
      setConvFieldIndex(i => Math.max(0, i - 1));
    };

    const scheduleAutoAdvance = (fieldId, newValue) => {
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = setTimeout(() => handleConvAdvanceWithValue(fieldId, newValue), 300);
    };

    // Update stable refs so the keyboard handler always calls current closures
    convAdvanceRef.current = handleConvAdvance;
    convBackRef.current = handleConvBack;
    convContextRef.current = { currentField, currentFormValues: formValues, onHandleChange: handleChange };

    // Large-format input renderer for conversational questions
    const renderConvInput = (field) => {
      if (!field) return null;
      const fid = field?.name || field?.id;
      const val = formValues?.[fid] ?? '';
      // Use state from computeEffectiveFieldState (includes dependency-driven disabled).
      // For the current interactive field use currentState; fall back to isFieldDisabled.
      const disabled = (field === currentField ? currentState?.disabled : undefined)
        ?? isFieldDisabled(field);
      const placeholder = field?.placeholder || '';
      const bigInput = {
        width: '100%', padding: '12px 16px', fontSize: '1.05rem',
        border: `2px solid ${errors?.[fid] ? '#ef4444' : accent}`,
        borderRadius: '8px', backgroundColor: 'transparent',
        color: labelColor, fontFamily, outline: 'none',
      };

      switch (field?.type) {
        case 'textarea':
          return (
            <textarea rows={4} placeholder={placeholder} disabled={disabled}
              value={val} onChange={e => handleChange(fid, e.target.value)}
              style={{ ...bigInput, resize: 'vertical' }} autoFocus />
          );
        case 'radio':
          return (
            <div className="space-y-2 mt-2">
              {(field?.options || []).map((opt, i) => {
                const v = typeof opt === 'object' ? opt?.value : opt;
                const l = typeof opt === 'object' ? opt?.label : opt;
                const sel = val === v;
                return (
                  <button key={i} type="button" disabled={disabled}
                    onClick={() => { handleChange(fid, v); scheduleAutoAdvance(fid, v); }}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all"
                    style={{ borderColor: sel ? accent : `${accent}40`, backgroundColor: sel ? `${accent}18` : 'transparent', color: labelColor, fontFamily }}>
                    <span className="w-7 h-7 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-xs font-bold"
                      style={{ borderColor: sel ? accent : '#9ca3af', backgroundColor: sel ? accent : 'transparent', color: '#fff' }}>
                      {sel ? <Icon name="Check" size={13} color="#fff" /> : String.fromCharCode(65 + i)}
                    </span>
                    <span className="text-sm font-medium">{l}</span>
                  </button>
                );
              })}
            </div>
          );
        case 'checkbox':
          return (
            <div className="space-y-2 mt-2">
              {(field?.options || []).map((opt, i) => {
                const v = typeof opt === 'object' ? opt?.value : opt;
                const l = typeof opt === 'object' ? opt?.label : opt;
                const arr = Array.isArray(val) ? val : [];
                const chk = arr.includes(v);
                return (
                  <button key={i} type="button" disabled={disabled}
                    onClick={() => { const nxt = chk ? arr.filter(x => x !== v) : [...arr, v]; handleChange(fid, nxt); }}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all"
                    style={{ borderColor: chk ? accent : `${accent}40`, backgroundColor: chk ? `${accent}18` : 'transparent', color: labelColor, fontFamily }}>
                    <span className="w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center"
                      style={{ borderColor: chk ? accent : '#9ca3af', backgroundColor: chk ? accent : 'transparent' }}>
                      {chk && <Icon name="Check" size={11} color="#fff" />}
                    </span>
                    <span className="text-sm font-medium">{l}</span>
                  </button>
                );
              })}
            </div>
          );
        case 'select': case 'dropdown':
          return (
            <select value={val} disabled={disabled}
              onChange={e => { handleChange(fid, e.target.value); if (e.target.value) scheduleAutoAdvance(fid, e.target.value); }}
              style={bigInput}>
              <option value="">{placeholder || 'Select an option'}</option>
              {(field?.options || []).map((opt, i) => (
                <option key={i} value={typeof opt === 'object' ? opt?.value : opt}>
                  {typeof opt === 'object' ? opt?.label : opt}
                </option>
              ))}
            </select>
          );
        case 'toggle': {
          const on = val === 'true' || val === true;
          return (
            <button type="button" role="switch" aria-checked={on} disabled={disabled}
              onClick={() => { const nv = String(!on); handleChange(fid, nv); scheduleAutoAdvance(fid, nv); }}
              className="flex items-center gap-4 mt-3">
              <div className="relative w-14 h-7 rounded-full transition-colors" style={{ backgroundColor: on ? accent : '#d1d5db' }}>
                <span className="absolute top-1.5 w-4 h-4 bg-white rounded-full shadow transition-all" style={{ left: on ? 'calc(100% - 20px)' : '6px' }} />
              </div>
              <span className="text-lg font-medium" style={{ color: labelColor, fontFamily }}>
                {on ? (field?.onLabel || 'On') : (field?.offLabel || 'Off')}
              </span>
            </button>
          );
        }
        case 'slider': {
          // Support both canonical (sliderMin) and compat (min) property names
          const sMin  = field?.sliderMin  ?? field?.min  ?? 0;
          const sMax  = field?.sliderMax  ?? field?.max  ?? 100;
          const sStep = field?.sliderStep ?? field?.step ?? 1;
          const sDef  = field?.sliderDefault ?? Math.round((sMin + sMax) / 2);
          const sVal  = val != null && val !== '' ? Number(val) : sDef;
          return (
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-4">
                <input type="range" min={sMin} max={sMax} step={sStep} value={sVal} disabled={disabled}
                  onChange={e => handleChange(fid, e.target.value)}
                  className="flex-1 h-2 rounded-lg cursor-pointer" style={{ accentColor: accent }} />
                <span className="text-2xl font-bold w-16 text-right" style={{ color: accent, fontFamily }}>{sVal}</span>
              </div>
              <div className="flex justify-between text-xs" style={{ color: `${labelColor}80` }}>
                <span>{sMin}</span><span>{sMax}</span>
              </div>
            </div>
          );
        }
        case 'rating': {
          const rMax = Math.max(1, field?.ratingMax ?? 5);
          const rDef = Math.min(rMax, Math.max(1, field?.ratingDefault ?? Math.ceil(rMax / 2)));
          const rVal = val != null && val !== '' ? Number(val) : rDef;
          return (
            <div className="flex items-center gap-1 mt-4 flex-wrap">
              {Array.from({ length: rMax }, (_, i) => {
                const starVal = i + 1;
                const isSel = starVal <= rVal;
                return (
                  <button key={i} type="button" disabled={disabled}
                    onClick={() => { handleChange(fid, starVal); scheduleAutoAdvance(fid, starVal); }}
                    style={{
                      fontSize: '36px', color: isSel ? accent : '#d1d5db',
                      background: 'none', border: 'none',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      padding: '0 4px', lineHeight: 1,
                      opacity: disabled ? 0.5 : 1, transition: 'color 0.15s',
                    }}>★</button>
                );
              })}
            </div>
          );
        }
        case 'scale': {
          const scMin = field?.scaleMin ?? 1;
          const scMax = Math.max(scMin + 1, field?.scaleMax ?? 10);
          const scDef = Math.min(scMax, Math.max(scMin, field?.scaleDefault ?? Math.round((scMin + scMax) / 2)));
          const scVal = val != null && val !== '' ? Number(val) : scDef;
          return (
            <div className="flex flex-wrap gap-2 mt-4">
              {Array.from({ length: scMax - scMin + 1 }, (_, i) => {
                const v = scMin + i;
                const isSel = v === scVal;
                return (
                  <button key={i} type="button" disabled={disabled}
                    onClick={() => { handleChange(fid, v); scheduleAutoAdvance(fid, v); }}
                    className="w-10 h-10 flex items-center justify-center rounded-xl border-2 text-sm font-bold transition-all"
                    style={{
                      borderColor: isSel ? accent : `${accent}40`,
                      backgroundColor: isSel ? accent : 'transparent',
                      color: isSel ? '#fff' : labelColor,
                      fontFamily,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                    }}>
                    {v}
                  </button>
                );
              })}
            </div>
          );
        }
        case 'signature':
          return (
            <PreviewConvSignaturePad
              value={val}
              onChange={v => handleChange(fid, v)}
              borderColor={errors?.[fid] ? '#ef4444' : accent}
              strokeColor={labelColor}
            />
          );
        default: {
          const idPrefix = (fid || '').split('_')[0];
          const nativeType = idPrefix === 'email' ? 'email' : idPrefix === 'phone' ? 'tel' : idPrefix === 'number' ? 'number' : idPrefix === 'date' ? 'date' : idPrefix === 'url' ? 'url' : 'text';
          return (
            <input type={nativeType} autoFocus placeholder={placeholder} disabled={disabled}
              value={val} onChange={e => handleChange(fid, e.target.value)} style={bigInput} />
          );
        }
      }
    };

    const hasValue = currentFieldValue !== undefined && currentFieldValue !== '' && !(Array.isArray(currentFieldValue) && currentFieldValue.length === 0);

    // ── Answer preview formatter for review overlay ──────────────────────────
    const formatAnswerPreview = (field, value) => {
      if (value === undefined || value === null || value === '') return '';
      const type = field?.type || 'input';

      if (type === 'checkbox') {
        if (!Array.isArray(value) || value.length === 0) return '';
        const opts = field?.options || [];
        const labels = value.map(v => {
          const match = opts.find(o => (typeof o === 'object' ? o?.value : o) === v);
          return match ? (typeof match === 'object' ? match?.label : match) : v;
        });
        return labels.join(', ');
      }

      if (type === 'radio' || type === 'select' || type === 'dropdown') {
        const opts = field?.options || [];
        const match = opts.find(o => (typeof o === 'object' ? o?.value : o) === value);
        return match ? (typeof match === 'object' ? match?.label : match) : String(value);
      }

      if (type === 'toggle') {
        return (value === 'true' || value === true)
          ? (field?.onLabel || 'On')
          : (field?.offLabel || 'Off');
      }

      if (type === 'signature') return 'Signed ✓';

      if (type === 'rating') return `${'★'.repeat(Number(value))}${'☆'.repeat(Math.max(0, (field?.ratingMax ?? 5) - Number(value)))}`;

      if (type === 'scale') return `${value} / ${field?.scaleMax ?? 10}`;

      const str = String(value);
      return str.length > 60 ? str.slice(0, 57) + '…' : str;
    };

    // Count answered questions (for showing the Review button)
    const answeredCount = questionScreens.filter(s => {
      if (!s.field) return false;
      const fid = s.field?.id || s.field?.name;
      const v = formValues?.[fid];
      return v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
    }).length;

    return (
      <div
        ref={convContainerRef}
        onKeyDown={handleConvKeyDown}
        tabIndex={-1}
        className="h-full flex flex-col relative outline-none"
        style={{ background: formBackground || '#f9fafb', fontFamily }}
      >
        {/* Slim progress bar + counter + back */}
        <div className="flex-shrink-0">
          <div className="h-1" style={{ backgroundColor: `${accent}25` }}>
            <div className="h-full transition-all duration-500" style={{ width: `${progressPct}%`, backgroundColor: accent }} />
          </div>
          <div className="flex items-center justify-between px-4 py-2 bg-white/80 dark:bg-black/20 backdrop-blur-sm border-b border-black/5">
            <button type="button" onClick={handleConvBack} disabled={safeIdx === 0}
              className="flex items-center gap-1 text-sm font-medium disabled:opacity-25 transition-opacity"
              style={{ color: accent }}>
              <Icon name="ChevronLeft" size={17} />Back
            </button>
            <span className="text-xs font-semibold tabular-nums" style={{ color: `${labelColor}70` }}>
              {safeIdx + 1} / {totalScreens}
            </span>
            {answeredCount > 0 ? (
              <button type="button" onClick={() => setConvReviewMode(true)}
                className="flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-80"
                style={{ color: accent }}>
                <Icon name="ListChecks" size={14} />
                Review
              </button>
            ) : (
              <button type="button" onClick={requestConvReset}
                className="flex items-center gap-1 text-xs font-medium opacity-50 hover:opacity-90 transition-opacity"
                style={{ color: accent }}
                title="Restart from the beginning">
                <Icon name="RotateCcw" size={13} />Restart
              </button>
            )}
          </div>
        </div>

        {/* Question area — vertically centred with overflow hidden to clip animations */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 relative overflow-hidden">
          {/* Outgoing card snapshot (exit animation) */}
          {convOutgoing && (
            <div className={`w-full max-w-lg absolute ${convOutgoing.exitClass}`} aria-hidden="true">
              {convOutgoing.screen?.prelude?.length > 0 && (
                <div className="mb-6 space-y-3 opacity-70">
                  {/* prelude items are { field, state } from the shared sequence builder */}
                  {convOutgoing.screen.prelude.map((item, i) => (
                    <div key={i}>{renderPreviewElement(item?.field ?? item)}</div>
                  ))}
                </div>
              )}
              {convOutgoing.screen?.field && (
                <h2 className="text-xl font-semibold leading-snug" style={{ color: labelColor }}>
                  {convOutgoing.screen.field?.label || convOutgoing.screen.field?.name}
                </h2>
              )}
            </div>
          )}

          {/* Incoming card */}
          <div key={safeIdx} className={`w-full max-w-lg ${animClass} ${convShake ? 'conv-shake' : ''}`}>
            {/* Prelude layout elements — items are { field, state } from shared builder */}
            {(currentScreen?.prelude || []).length > 0 && (
              <div className="mb-6 space-y-3">
                {currentScreen.prelude.map((item, i) => (
                  <div key={i}>{renderPreviewElement(item?.field ?? item)}</div>
                ))}
              </div>
            )}

            {currentField && (
              <div className="space-y-5">
                <div className="flex items-start gap-2">
                  <span className="text-sm font-bold mt-0.5 flex-shrink-0" style={{ color: `${accent}cc` }}>
                    {safeIdx + 1} →
                  </span>
                  <div>
                    <h2 className="text-xl font-semibold leading-snug" style={{ color: labelColor }}>
                      {currentField?.label || currentField?.name}
                      {(currentState?.required ?? currentField?.required) && <span className="text-red-500 ml-1">*</span>}
                    </h2>
                    {currentField?.description && (
                      <p className="text-sm mt-1 opacity-60" style={{ color: labelColor }}>{currentField.description}</p>
                    )}
                  </div>
                </div>

                <div>
                  {renderConvInput(currentField)}
                  {currentError && (
                    <p className="flex items-center gap-1.5 mt-2 text-sm text-red-500">
                      <Icon name="AlertCircle" size={14} />{currentError}
                    </p>
                  )}

                  {/* OK / Submit button for text/textarea/slider/checkbox */}
                  {showOkButton && (
                    <div className="flex items-center gap-3 mt-5">
                      <button type="button" onClick={handleConvAdvance}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold transition-all shadow-sm"
                        style={{ backgroundColor: accent }}>
                        {isLastScreen
                          ? <>{submitText} <Icon name="Check" size={15} /></>
                          : <>{hasValue ? 'OK' : 'Skip'} <Icon name="Check" size={15} /></>}
                      </button>
                      {!isLastScreen && (
                        <span className="text-xs" style={{ color: `${labelColor}50` }}>
                          press <kbd className="px-1.5 py-0.5 rounded border text-xs" style={{ borderColor: `${labelColor}30` }}>Enter ↵</kbd>
                        </span>
                      )}
                    </div>
                  )}

                  {/* Auto-advance types: Submit only on last screen; skip link otherwise */}
                  {!showOkButton && isLastScreen && (
                    <div className="mt-6">
                      <button type="button" onClick={handleConvAdvance}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-sm"
                        style={{ backgroundColor: accent }}>
                        {submitText} <Icon name="Check" size={15} />
                      </button>
                    </div>
                  )}
                  {!showOkButton && !isLastScreen && !currentField?.required && (
                    <button type="button" onClick={handleConvAdvance}
                      className="mt-4 text-xs flex items-center gap-1 opacity-40 hover:opacity-70 transition-opacity"
                      style={{ color: labelColor }}>
                      Skip <Icon name="ChevronRight" size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Layout-only trailing screen */}
            {!currentField && (
              <div className="text-center mt-4">
                <button type="button" onClick={handleConvAdvance}
                  className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl text-white text-sm font-semibold shadow-sm"
                  style={{ backgroundColor: accent }}>
                  {isLastScreen ? <>{submitText} <Icon name="Check" size={15} /></> : <>Continue <Icon name="ChevronRight" size={15} /></>}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Conversational mode badge — bottom-right, unobtrusive */}
        <div
          className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full pointer-events-none select-none"
          style={{
            backgroundColor: `${accent}18`,
            border: `1px solid ${accent}35`,
          }}
          aria-hidden="true"
        >
          <Icon name="MessageSquare" size={11} style={{ color: accent, opacity: 0.75 }} />
          <span className="text-xs font-medium" style={{ color: accent, opacity: 0.75, letterSpacing: '0.01em' }}>
            Conversational mode · preview
          </span>
        </div>

        {/* ── Review-answers overlay ────────────────────────────────────────── */}
        {convReviewMode && (
          <div
            className="absolute inset-0 z-50 flex flex-col"
            style={{ background: formBackground || '#f9fafb', fontFamily }}
          >
            {/* Header */}
            <div className="relative z-10 flex items-center justify-between px-4 py-3 bg-white/90 backdrop-blur-sm border-b border-black/8">
              <div className="flex items-center gap-2">
                <Icon name="ListChecks" size={18} color={accent} />
                <h2 className="text-base font-semibold" style={{ color: labelColor }}>Review your answers</h2>
              </div>
              <button
                type="button"
                onClick={() => setConvReviewMode(false)}
                className="p-1.5 rounded-lg transition-colors hover:bg-black/8"
                style={{ color: labelColor }}
                aria-label="Close review"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Scrollable question list */}
            <div className="relative z-10 flex-1 overflow-y-auto py-4 px-4 space-y-2">
              {questionScreens.map((screen, idx) => {
                if (!screen.field) return null;
                const fid = screen.field?.id || screen.field?.name;
                const val = formValues?.[fid];
                const hasAnswer = val !== undefined && val !== '' && !(Array.isArray(val) && val.length === 0);
                const answerPreview = hasAnswer ? formatAnswerPreview(screen.field, val) : '';
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setConvFieldIndex(idx);
                      setConvDirection('forward');
                      setConvReviewMode(false);
                    }}
                    className="w-full text-left rounded-xl border-2 px-4 py-3 transition-all hover:shadow-sm"
                    style={{
                      borderColor: hasAnswer ? `${accent}50` : `${accent}1a`,
                      backgroundColor: hasAnswer ? `${accent}08` : 'rgba(255,255,255,0.6)',
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium" style={{ color: labelColor }}>
                          <span className="font-normal opacity-40 mr-1">{idx + 1}.</span>
                          {screen.field.label || screen.field.name}
                          {screen.state?.required && <span className="text-red-500 ml-0.5">*</span>}
                        </p>
                        {hasAnswer && answerPreview && (
                          <p className="text-sm mt-0.5 truncate font-medium" style={{ color: accent }}>
                            {answerPreview}
                          </p>
                        )}
                        {!hasAnswer && (
                          <p className="text-xs mt-0.5 opacity-35 italic" style={{ color: labelColor }}>
                            Not answered
                          </p>
                        )}
                      </div>
                      <Icon name="Pencil" size={13} color={accent} className="flex-shrink-0 mt-1 opacity-50" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer: continue or go to submit */}
            <div className="relative z-10 px-4 py-4 bg-white/85 backdrop-blur-sm border-t border-black/5 flex flex-col gap-2">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConvReviewMode(false)}
                  className="flex-1 py-2.5 rounded-xl border-2 text-sm font-semibold transition-colors"
                  style={{ borderColor: `${accent}50`, color: accent, backgroundColor: 'transparent' }}
                >
                  Continue
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConvFieldIndex(totalScreens - 1);
                    setConvDirection('forward');
                    setConvReviewMode(false);
                  }}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors shadow-sm"
                  style={{ backgroundColor: accent }}
                >
                  Go to Submit
                </button>
              </div>
              <button
                type="button"
                onClick={handleReset}
                className="w-full py-1.5 text-xs font-medium opacity-50 hover:opacity-75 transition-opacity"
                style={{ color: labelColor }}
              >
                Restart from beginning
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-background/80 z-[1040] flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-elevation-xl border border-border w-full max-h-[90vh] flex flex-col relative" style={{ maxWidth: isSplit ? 'min(1100px, 95vw)' : `min(${gt?.formMaxWidth ? gt.formMaxWidth + 64 : 768}px, 95vw)` }}>
        {/* Header */}
        <div className="p-4 lg:p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="Eye" size={24} color="var(--color-primary)" />
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">
                {formTitle || 'Form Preview'}
              </h2>
              <p className="text-xs text-muted-foreground">
                {isConversational
                  ? 'Conversational preview — one question at a time · submissions won\'t be recorded'
                  : isSplit
                  ? 'Split-page preview — two columns side by side'
                  : isMultiStep
                  ? `Multi-step preview — Step ${safeStep + 1} of ${steps?.length}`
                  : 'Live preview with validation — fill in the form to test rules'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
            aria-label="Close preview"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Body — flex wrapper adds side panel for split layout */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Split content panel — left side */}
          {isSplit && (splitPanelConfig?.panelSide || 'left') === 'left' && (
            <div style={{ flex: `0 0 ${Math.min(80, Math.max(20, splitPanelConfig?.widthRatio || 40))}%`, overflow: 'hidden', background: 'var(--color-muted)', borderRight: '1px solid var(--color-border)', minHeight: 0 }}>
              <SplitPanelContent config={splitPanelConfig} />
            </div>
          )}
          {/* Form column */}
          <div
            className={`relative flex-1 min-w-0 overflow-x-auto scrollbar-custom ${isConversational && !submitted && previewPaymentStep !== 'payment' ? 'overflow-hidden p-0' : 'overflow-y-auto p-4 lg:p-6'}`}
            style={isConversational && !submitted && previewPaymentStep !== 'payment' ? {} : formContainerStyle}
          >
          {/* Layout mode badge — mirrors the builder canvas badge so creators
              always know which mode is active mid-preview. Standard mode renders
              nothing (LayoutModeBadge returns null). Conversational uses an
              absolute overlay so it doesn't disturb the full-height flow. */}
          {!submitted && previewPaymentStep !== 'payment' && (
            isConversational ? (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
                <LayoutModeBadge layoutMode="conversational" />
              </div>
            ) : isMultiStep ? (
              <LayoutModeBadge layoutMode="multi-step" stepCount={steps?.length} />
            ) : null
          )}
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${gt?.accentColor || '#0e7572'}1a` }}
              >
                <Icon name="CheckCircle2" size={36} color={gt?.accentColor || '#0e7572'} />
              </div>
              <h3
                className="text-lg font-heading font-semibold"
                style={{ color: gt?.labelColor || '#111827' }}
              >
                Form Submitted Successfully!
              </h3>
              <p
                className="text-sm text-center max-w-sm"
                style={{ color: gt?.inputTextColor || gt?.labelColor || '#6b7280' }}
              >
                All validation rules passed. This is a preview — no data was actually saved.
              </p>
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border transition-colors"
                style={{
                  color: gt?.accentColor || '#0e7572',
                  borderColor: `${gt?.accentColor || '#0e7572'}4d`,
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${gt?.accentColor || '#0e7572'}1a`; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Icon name="RotateCcw" size={14} />
                Reset &amp; Try Again
              </button>
            </div>
          ) : previewPaymentStep === 'payment' ? (
            /* ── Mock payment step — display-only, no real Stripe mount ── */
            <div className="mx-auto space-y-5" style={{ maxWidth: gt?.formMaxWidth ? `${gt.formMaxWidth}px` : '576px' }}>
              {/* Preview badge */}
              <div className="flex items-center justify-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5">
                <Icon name="Eye" size={15} className="text-amber-600 shrink-0" />
                <p className="text-xs text-amber-700 font-medium">
                  Preview mode — no real payment will be charged
                </p>
              </div>

              {/* Amount + description summary */}
              <div className="text-center space-y-1" style={{ fontFamily: gt?.fontFamily || undefined }}>
                <h3 className="text-base font-semibold text-foreground" style={{ fontFamily: gt?.fontFamily || undefined }}>Complete Your Payment</h3>
                {paymentSettings?.description && (
                  <p className="text-sm text-muted-foreground">{paymentSettings.description}</p>
                )}
                <p className="text-2xl font-bold" style={{ color: gt?.accentColor || 'var(--color-primary)', fontFamily: gt?.fontFamily || undefined }}>
                  {(() => {
                    const amt = paymentSettings?.mode === 'variable'
                      ? parseFloat(previewPaymentAmount)
                      : parseFloat(paymentSettings?.amount);
                    const cur = paymentSettings?.currency || 'USD';
                    if (!amt || isNaN(amt)) return cur + ' —';
                    try { return paymentsService.formatAmount(amt, cur); } catch { return `${cur} ${amt.toFixed(2)}`; }
                  })()}
                </p>
                <p className="text-xs text-muted-foreground">
                  Currency: {paymentSettings?.currency || 'USD'}
                  {paymentSettings?.mode === 'variable' && ' • Variable amount'}
                </p>
              </div>

              {/* Mock Stripe card form */}
              {(() => {
                // Only use hex accent for alpha-appending (CSS var() strings can't take hex alpha suffixes)
                const accentHex = gt?.accentColor && gt.accentColor.startsWith('#') ? gt.accentColor : null;
                const cardBg = gt?.backgroundColor || undefined;
                const cardRadius = gt?.borderRadius != null ? `${gt.borderRadius}px` : undefined;
                const cardFont = gt?.fontFamily || undefined;
                const inputBg = gt?.inputBackground || undefined;
                const inputText = gt?.inputTextColor || undefined;
                const labelCol = gt?.labelColor || undefined;

                // Input style: prefer theme colours; fall back to CSS vars / Tailwind-friendly values
                const mockInputStyle = {
                  backgroundColor: inputBg || undefined,
                  color: inputText || undefined,
                  borderColor: accentHex || undefined,
                  borderRadius: cardRadius || undefined,
                  fontFamily: cardFont || undefined,
                };

                // Card container border: use hex alpha when available, else fall back to CSS var
                const cardBorderColor = accentHex ? `${accentHex}40` : 'var(--color-border)';

                // Pay button: hex accent at ~40% opacity when available, else standard primary
                const payBtnStyle = accentHex
                  ? { backgroundColor: `${accentHex}66`, color: '#ffffff', opacity: 0.85, borderRadius: cardRadius || '0.375rem', fontFamily: cardFont }
                  : { borderRadius: cardRadius || '0.375rem', fontFamily: cardFont };
                const payBtnClass = accentHex
                  ? 'w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-medium cursor-not-allowed'
                  : 'w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-md bg-primary/40 text-primary-foreground/60 text-sm font-medium cursor-not-allowed';

                return (
              <div
                className="border p-6 space-y-4"
                style={{
                  backgroundColor: cardBg || 'var(--color-card)',
                  borderColor: cardBorderColor,
                  borderRadius: cardRadius || '0.5rem',
                  fontFamily: cardFont,
                }}
              >
                <p className="text-sm font-medium text-card-foreground" style={{ fontFamily: cardFont }}>Payment Details</p>

                {/* Card number row */}
                <div className="space-y-1.5">
                  <label
                    className="text-xs font-medium uppercase tracking-wide"
                    style={{ color: labelCol || undefined, fontFamily: cardFont }}
                  >
                    Card number
                  </label>
                  <div className="relative">
                    <input
                      readOnly
                      value="1234  5678  9012  3456"
                      className={`w-full px-3 py-2.5 border text-sm cursor-not-allowed ${!inputBg ? 'bg-muted/40' : ''} ${!inputText ? 'text-muted-foreground' : ''}`}
                      style={mockInputStyle}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1">
                      <span className="text-xs font-bold text-blue-600">VISA</span>
                    </div>
                  </div>
                </div>

                {/* Expiry + CVC row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-medium uppercase tracking-wide"
                      style={{ color: labelCol || undefined, fontFamily: cardFont }}
                    >
                      Expiry
                    </label>
                    <input
                      readOnly
                      value="MM / YY"
                      className={`w-full px-3 py-2.5 border text-sm cursor-not-allowed ${!inputBg ? 'bg-muted/40' : ''} ${!inputText ? 'text-muted-foreground' : ''}`}
                      style={mockInputStyle}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      className="text-xs font-medium uppercase tracking-wide"
                      style={{ color: labelCol || undefined, fontFamily: cardFont }}
                    >
                      CVC
                    </label>
                    <input
                      readOnly
                      value="•••"
                      className={`w-full px-3 py-2.5 border text-sm cursor-not-allowed ${!inputBg ? 'bg-muted/40' : ''} ${!inputText ? 'text-muted-foreground' : ''}`}
                      style={mockInputStyle}
                    />
                  </div>
                </div>

                {/* Pay button (disabled in preview) */}
                <button
                  type="button"
                  disabled
                  className={payBtnClass}
                  style={payBtnStyle}
                >
                  <Icon name="CreditCard" size={16} />
                  {(() => {
                    const amt = paymentSettings?.mode === 'variable'
                      ? parseFloat(previewPaymentAmount)
                      : parseFloat(paymentSettings?.amount);
                    const cur = paymentSettings?.currency || 'USD';
                    try { return `Pay ${paymentsService.formatAmount(amt, cur)}`; } catch { return 'Pay'; }
                  })()}
                </button>

                <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                  <Icon name="Shield" size={13} />
                  <span>Secured by Stripe • Your payment information is encrypted</span>
                </div>
              </div>
                );
              })()}

              <button
                type="button"
                onClick={() => setPreviewPaymentStep('form')}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                ← Back to form
              </button>
            </div>
          ) : isConversational ? (
            /* ── Conversational mode — one question at a time ── */
            renderConvPreview()
          ) : isFooterBar ? (
            /* ── Footer-bar mode: card + footer strip ── */
            <div
              className="mx-auto rounded-lg border border-border overflow-hidden"
              style={{
                maxWidth: globalTheme?.formMaxWidth ? `${globalTheme.formMaxWidth}px` : '512px',
                borderBottomLeftRadius: `${multiStepConfig?.footerBorderRadius ?? 0}px`,
                borderBottomRightRadius: `${multiStepConfig?.footerBorderRadius ?? 0}px`,
              }}
            >
              {/* Form fields */}
              <div className="pt-4 px-4 pb-0 space-y-4" style={{ background: gt?.backgroundColor || '#ffffff', fontFamily: gt?.fontFamily || undefined }}>
                {isMultiStep && multiStepConfig?.showStepTitles !== false && currentStepObj?.title && (
                  <div className="pb-3 border-b border-border">
                    <h3 className="text-base font-heading font-semibold text-foreground">{currentStepObj?.title}</h3>
                    {currentStepObj?.description && (
                      <p className="text-sm text-muted-foreground mt-0.5">{currentStepObj?.description}</p>
                    )}
                  </div>
                )}
                {(formElements || [])?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="FileText" size={48} color="var(--color-muted-foreground)" />
                    <p className="text-sm text-muted-foreground mt-4">No elements to preview. Add elements to your form first.</p>
                  </div>
                ) : (
                  visibleElements?.map((element) => (
                    <div key={element?.id} className="py-0.5">{renderPreviewElement(element)}</div>
                  ))
                )}
              </div>
              {/* Footer bar strip with Prev / Next / Submit inside */}
              <div
                className="flex items-center justify-between px-6 py-3 mt-6 relative"
                style={{ background: footerBarBg }}
              >
                {!isFirstStep ? (
                  <button type="button" onClick={handlePrev} className="flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white transition-colors">
                    <Icon name="ChevronLeft" size={16} />
                    {multiStepConfig?.prevButtonText || 'Previous'}
                  </button>
                ) : <span />}
                {showStepCountFB && (
                  <span className="text-xs text-white/60 absolute left-1/2 -translate-x-1/2 pointer-events-none">
                    Step {safeStep + 1} of {steps?.length}
                  </span>
                )}
                <button type="button" onClick={isLastStep ? handleSubmit : handleNext} className="flex items-center gap-1.5 text-sm font-semibold text-white hover:text-white/80 transition-colors">
                  {isLastStep ? (multiStepConfig?.submitButtonText || 'Submit') : (multiStepConfig?.nextButtonText || 'Next')}
                  {!isLastStep && <Icon name="ChevronRight" size={16} />}
                </button>
              </div>
            </div>
          ) : (
            /* ── Normal (bar / steps / numbered / none) mode ── */
            <div className="mx-auto space-y-4" style={{ maxWidth: gt?.formMaxWidth ? `${gt.formMaxWidth}px` : '576px' }}>
              {isMultiStep && multiStepConfig?.progressPosition !== 'bottom' && renderProgress()}
              {isMultiStep && multiStepConfig?.showStepTitles !== false && currentStepObj?.title && (
                <div className="pb-3 border-b border-border">
                  <h3 className="text-base font-heading font-semibold text-foreground">{currentStepObj?.title}</h3>
                  {currentStepObj?.description && (
                    <p className="text-sm text-muted-foreground mt-0.5">{currentStepObj?.description}</p>
                  )}
                </div>
              )}
              {(formElements || [])?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="FileText" size={48} color="var(--color-muted-foreground)" />
                  <p className="text-sm text-muted-foreground mt-4">No elements to preview. Add elements to your form first.</p>
                </div>
              ) : (
                visibleElements?.map((element) => (
                  <div key={element?.id} className="py-0.5">{renderPreviewElement(element)}</div>
                ))
              )}
              {isMultiStep && multiStepConfig?.progressPosition === 'bottom' && (
                <div className="mt-4">{renderProgress()}</div>
              )}
            </div>
          )}
          </div>
          {/* Split content panel — right side */}
          {isSplit && (splitPanelConfig?.panelSide || 'left') === 'right' && (
            <div style={{ flex: `0 0 ${Math.min(80, Math.max(20, splitPanelConfig?.widthRatio || 40))}%`, overflow: 'hidden', background: 'var(--color-muted)', borderLeft: '1px solid var(--color-border)', minHeight: 0 }}>
              <SplitPanelContent config={splitPanelConfig} />
            </div>
          )}
        </div>

        {/* Footer — Prev/Next are inside the footer bar strip when isFooterBar; only Close shown there */}
        {!submitted && (
          <div className="border-t border-border">
            {(gt?.formMaxWidth ?? 576) > 480 &&
              (gt?.formMaxWidth ?? 576) !== gt?.mobileWarningDismissedWidth && (
              <div className="flex items-start gap-2 px-4 lg:px-6 py-2.5 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800">
                <Icon name="AlertTriangle" size={14} color="var(--color-amber-600, #d97706)" className="mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-400 flex-1">
                  Your form width is set to <strong>{gt.formMaxWidth}px</strong> — widths above 480px may overflow on small screens and require horizontal scrolling on mobile.
                </p>
                {onUpdateTheme && (
                  <button
                    type="button"
                    onClick={() => onUpdateTheme({ ...gt, mobileWarningDismissedWidth: gt?.formMaxWidth ?? 576 })}
                    className="ml-1 text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-200 transition-colors shrink-0"
                    title="Dismiss this warning"
                  >
                    <Icon name="X" size={14} />
                  </button>
                )}
              </div>
            )}
          <div className="p-4 lg:p-6 space-y-3">
            {/* Variable-amount input — shown in footer when payment is variable-mode and on last/only step */}
            {!submitted && previewPaymentStep !== 'payment' && paymentEnabled && paymentSettings?.mode === 'variable' && (!isMultiStep || isLastStep) && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-foreground">
                  Payment Amount ({paymentSettings?.currency || 'USD'}) <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-muted-foreground text-sm">$</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={previewPaymentAmount}
                    onChange={(e) => setPreviewPaymentAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-7 pr-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                {errors?._payment && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} />
                    {errors._payment}
                  </p>
                )}
              </div>
            )}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={onClose}>Close</Button>
                {/* Conversational mode: navigation is inside the conv view; show Reset instead */}
                {isConversational && !submitted && (
                  <Button variant="outline" onClick={requestConvReset} iconName="RotateCcw" iconPosition="left">
                    Reset
                  </Button>
                )}
                {!isConversational && !isFooterBar && isMultiStep && safeStep > 0 && previewPaymentStep !== 'payment' && (
                  <Button variant="outline" onClick={handlePrev} iconName="ChevronLeft" iconPosition="left">
                    {multiStepConfig?.prevButtonText || 'Previous'}
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!isConversational && Object.keys(errors)?.filter(k => k !== '_payment')?.length > 0 && (
                  <span className="text-xs text-destructive flex items-center gap-1">
                    <Icon name="AlertCircle" size={13} />
                    {Object.keys(errors)?.filter(k => k !== '_payment')?.length} error{Object.keys(errors)?.filter(k => k !== '_payment')?.length > 1 ? 's' : ''}
                  </span>
                )}
                {!isConversational && !isFooterBar && previewPaymentStep !== 'payment' && (
                  isMultiStep && safeStep < steps?.length - 1 ? (
                    <Button variant="default" iconName="ChevronRight" iconPosition="right" onClick={handleNext}>
                      {multiStepConfig?.nextButtonText || 'Next'}
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      iconName={paymentEnabled ? 'CreditCard' : 'Send'}
                      iconPosition="left"
                      onClick={handleSubmit}
                    >
                      {paymentEnabled
                        ? 'Continue to Payment'
                        : isMultiStep ? (multiStepConfig?.submitButtonText || 'Submit') : 'Submit Form'}
                    </Button>
                  )
                )}
              </div>
            </div>
          </div>
          </div>
        )}
        {/* Restart/Reset confirmation for conversational preview */}
        {convConfirmRestart && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/30" role="dialog" aria-modal="true">
            <div className="w-72 rounded-lg border border-black/10 bg-white shadow-xl p-4">
              <p className="text-sm text-gray-800 mb-3">Start over? Your answers will be cleared.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setConvConfirmRestart(false)}>
                  Cancel
                </Button>
                <Button variant="default" size="sm" onClick={handleReset}>
                  Start over
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewModal;